import { createContext, useContext, type MouseEvent } from 'react';
import { prioritizePhoneNumbers } from '@/lib/phone-type';
import type { CallRecord } from '@/store/features/telephony.feature';

/**
 * The browser call the rep is on, if any. One at a time.
 *
 * checking   mic and the server's checks (do-not-call, hours, credits)
 * connecting the browser is connecting to Twilio
 * ringing    the lead's phone is ringing
 * live       the lead answered
 * reconnecting  the network dropped; the SDK is trying to get it back
 * ending     hung up; the server is settling the call
 * ended      done: `result` holds the call as the server recorded it
 * blocked    the server refused it before dialing (`error` says why)
 * failed     the browser couldn't place it (mic, network)
 */
export type CallPhase =
  | 'checking'
  | 'connecting'
  | 'ringing'
  | 'live'
  | 'reconnecting'
  | 'ending'
  | 'ended'
  | 'blocked'
  | 'failed';

export interface StartCallOptions {
  /** The number to dial, as shown (the server parses it). */
  to: string;
  /** The call task it is for: the task panel completes it with this call. */
  taskId?: string;
  /** The lead it is for, when there is no task. */
  leadId?: string;
  /** Who is being called, for the call bar. */
  label?: string;
}

export interface ActiveCall extends StartCallOptions {
  phase: CallPhase;
  callId?: string;
  /** Credits a connected minute costs, once the server has priced it. */
  rateCredits?: number;
  /** The org number the call goes out from (the one nearest the lead). */
  fromE164?: string;
  /** When the lead answered (ms since epoch), for the timer. */
  answeredAt?: number;
  muted: boolean;
  error?: string;
  /** The server's record once the call has ended. */
  result?: CallRecord;
}

/** The rep's browser in their parallel dial session's room. */
export interface DialRoom {
  sessionId: string;
  phase: 'connecting' | 'live';
  muted: boolean;
}

export interface CallApi {
  /** This rep can place calls from Cleanlist now. */
  available: boolean;
  call: ActiveCall | null;
  /** The last call that ended, for the task panel to pick up. */
  ended: CallRecord | null;
  /** Resolves true once the call is placed, false when refused. */
  start: (options: StartCallOptions) => Promise<boolean>;
  hangUp: () => void;
  toggleMute: () => void;
  sendDigits: (digits: string) => void;
  /** Close the bar (after a call, or a refusal). */
  dismiss: () => void;
  /** Open the wrap-up for a call that ended earlier (the unsaved banner). */
  review: (call: CallRecord) => void;
  /** A call that ran outside this provider (a parallel leg) ended: the task
   *  panel picks it up as it does a single call's. */
  finished: (call: CallRecord) => void;
  /** Parallel dialing: the rep's browser in the session's room. */
  room: DialRoom | null;
  /** Resolves true once the browser is connecting to the room. */
  joinRoom: (sessionId: string) => Promise<boolean>;
  leaveRoom: () => void;
  toggleRoomMute: () => void;
}

const NONE: CallApi = {
  available: false,
  call: null,
  ended: null,
  start: async () => false,
  hangUp: () => {},
  toggleMute: () => {},
  sendDigits: () => {},
  dismiss: () => {},
  review: () => {},
  finished: () => {},
  room: null,
  joinRoom: async () => false,
  leaveRoom: () => {},
  toggleRoomMute: () => {},
};

export const CallContext = createContext<CallApi>(NONE);

export function useCall(): CallApi {
  return useContext(CallContext);
}

export const ACTIVE_PHASES: ReadonlySet<CallPhase> = new Set([
  'checking',
  'connecting',
  'ringing',
  'live',
  'reconnecting',
  'ending',
]);

/**
 * A phone link's click: places the call from Cleanlist when the rep can,
 * otherwise undefined so the `tel:` link opens the phone app as before.
 */
export function useCallClick(
  options: StartCallOptions | null
): ((event: MouseEvent) => void) | undefined {
  const { available, call, start } = useCall();
  if (!available || !options) return undefined;
  return (event) => {
    event.preventDefault();
    if (call && ACTIVE_PHASES.has(call.phase)) return;
    void start(options);
  };
}

/**
 * A person page's phone chip (people search, the person drawer): calls the
 * first number the chip shows, for that person.
 */
export function usePersonCallClick(person: {
  /** The lead behind the directory row (its `id` is an email key). */
  lead_id: string | null;
  name?: string | null;
  phone?: string | null;
  personal_phone_numbers?: Parameters<typeof prioritizePhoneNumbers>[0];
  phone_metadata?: Parameters<typeof prioritizePhoneNumbers>[1];
}): ((event: MouseEvent) => void) | undefined {
  const phone =
    prioritizePhoneNumbers(
      person.personal_phone_numbers,
      person.phone_metadata
    )[0] ?? person.phone;
  return useCallClick(
    phone
      ? {
          to: phone,
          leadId: person.lead_id ?? undefined,
          label: person.name ?? undefined,
        }
      : null
  );
}
